import { Alert, Select, Spin, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import type {
    FilterValue,
    SorterResult,
    TablePaginationConfig,
} from 'antd/lib/table/interface';
import { observer } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import {
    CAMPAIGN_ACTIVITY_TYPES,
    CampaignActivityClub,
    CampaignActivityItem,
    CampaignActivityType,
    Client,
    PaginatedCampaignActivity,
} from '../../../stores/client';
import { useClientStore } from '../../../contexts/mobx';
import { Tag } from '../../tag';
import TicketIcon from '../../../icons/Ticket.svg?react';
import { Button } from '../../button';
import { NotificationManager } from '../../notification-manager';
import { AddVisitModal } from './add-visit-modal';
import style from './style.module.scss';

type CampaignActivityTabProps = {
    clientObject?: Client;
};

type Filters = {
    types: CampaignActivityType[];
    sort: 'asc' | 'desc';
    page: number;
    perPage: number;
};

const INITIAL_FILTERS: Filters = {
    types: [],
    sort: 'desc',
    page: 1,
    perPage: 10,
};

const TYPE_LABELS: Record<CampaignActivityType, string> = {
    visit: 'Visit',
    join: 'Join',
    milestone_reached: 'Milestone reached',
    coupon_awarded: 'Coupon awarded',
    coupon_redeemed: 'Redeemed coupon',
};

const TIME_FORMAT = 'h:mm A';
const DATE_FORMAT = 'MMM D, YYYY';

// Some thumbnails are a bare filename rather than a URL, which 404s to a
// broken-image glyph; same guard the Coupons tab applies.
const isAbsoluteImageUrl = (value: string) => /^https?:\/\//i.test(value);

function visitDescription(item: CampaignActivityItem): string {
    const visit = item.visit;
    if (!visit) {
        return '';
    }
    if (visit.countedDelta === 0) {
        return visit.rejectionLabel ?? 'Not counted';
    }
    if (visit.source === 'ENROLLMENT') {
        return 'Joining bonus';
    }
    if (visit.source === 'CS_GRANT') {
        return 'Added by customer service';
    }
    if (typeof visit.totalPrice !== 'number') {
        return 'No transaction total';
    }
    return `$${visit.totalPrice.toFixed(2)} transaction total`;
}

function auditLine(csGrant: { agentEmail: string; grantedAt: string }): string {
    return `by ${csGrant.agentEmail} on ${moment(csGrant.grantedAt).format(
        'MMM D, YYYY, h:mm A'
    )}`;
}

function describe(item: CampaignActivityItem, clubName: string): string {
    switch (item.type) {
        case 'join':
            return `Joined ${clubName}`;
        case 'milestone_reached':
            // The threshold, not the ladder position: CS reasons in visit
            // counts ("the milestone at 5 visits"), and the campaign's own
            // copy names milestones that way.
            return `Milestone ${item.milestone?.threshold ?? ''} reached`;
        case 'visit':
            // The agent's note takes the line the transaction total would use:
            // a CS grant has no basket, and the reason is what CS came to read.
            return item.visit?.csGrant?.note ?? visitDescription(item);
        default: {
            // Both coupon types resolve the same promotion, so without the
            // label the awarded row and the redeemed row read identically and
            // differ only by date.
            const label = TYPE_LABELS[item.type];
            return item.coupon?.description
                ? `${label} — ${item.coupon.description}`
                : label;
        }
    }
}

function renderThumbnail(record: CampaignActivityItem) {
    const thumbnail = record.coupon?.thumbnail;
    return thumbnail && isAbsoluteImageUrl(thumbnail) ? (
        <img alt="" src={thumbnail} className={style.thumbnail} />
    ) : (
        <span className={style.no_image} />
    );
}

export const UserCampaignActivityTab = observer(
    ({ clientObject }: CampaignActivityTabProps) => {
        const clientStore = useClientStore();

        const [clubs, setClubs] = React.useState<CampaignActivityClub[]>([]);
        const [selectedClubId, setSelectedClubId] = React.useState<
            number | null
        >(null);
        const [isLoadingClub, setIsLoadingClub] = React.useState(true);
        const [clubFailed, setClubFailed] = React.useState(false);
        const [
            activity,
            setActivity,
        ] = React.useState<PaginatedCampaignActivity | null>(null);
        const [isFetching, setIsFetching] = React.useState(false);
        const [activityFailed, setActivityFailed] = React.useState(false);
        const [filters, setFilters] = React.useState<Filters>(INITIAL_FILTERS);
        const [addVisitOpen, setAddVisitOpen] = React.useState(false);
        const [isAddingVisit, setIsAddingVisit] = React.useState(false);

        // Guards against out-of-order responses within one client (a page or
        // filter change). Switching client is handled by the `key` on this
        // component in UserProfile, which remounts it and starts over.
        const latestRequestId = React.useRef(0);

        React.useEffect(() => {
            let cancelled = false;

            const loadClubs = async () => {
                setIsLoadingClub(true);
                const found = await clientStore.fetchCampaignActivityClubs();
                if (cancelled) {
                    return;
                }
                // A failed request is reported as such, never as "no campaign
                // is configured": that diagnosis sends the reader to the club
                // config instead of to the network.
                setClubFailed(found === null);
                setClubs(found ?? []);
                setSelectedClubId(found?.[0]?.id ?? null);
                setIsLoadingClub(false);
            };

            void loadClubs();
            return () => {
                cancelled = true;
            };
        }, [clientStore]);

        const club = clubs.find(({ id }) => id === selectedClubId) ?? null;
        const clubId = club?.id;

        const fetchActivity = React.useCallback(async () => {
            if (!clientObject?.id || !clubId) {
                return;
            }
            const requestId = ++latestRequestId.current;
            setIsFetching(true);

            const result = await clientStore.fetchCampaignActivity({
                clientId: clientObject.id,
                clubId,
                types: filters.types,
                sort: filters.sort,
                page: filters.page,
                perPage: filters.perPage,
            });

            // A superseded response touches neither rows nor spinner: clearing
            // them here drops the loading state while a newer request is still
            // in flight.
            if (requestId !== latestRequestId.current) {
                return;
            }
            setIsFetching(false);
            // Keeping the previous rows would show them under the filters of
            // the request that failed, which reads as a result for those.
            setActivityFailed(!result);
            setActivity(result);
        }, [clientObject?.id, clubId, filters, clientStore]);

        React.useEffect(() => {
            void fetchActivity();
        }, [fetchActivity]);

        const canAddVisit = Boolean(
            clientObject?.isPhoneNumberConfirmed && club
        );

        const columns: ColumnsType<CampaignActivityItem> = [
            {
                title: 'Date',
                key: 'occurredAt',
                width: 110,
                sorter: true,
                sortDirections: ['descend', 'ascend'] as const,
                // Antd derives its tooltip from its own three-step cycle, so it
                // offered "Click to cancel sorting" while this column only ever
                // reverses.
                showSorterTooltip: false,
                sortOrder:
                    filters.sort === 'asc'
                        ? ('ascend' as const)
                        : ('descend' as const),
                render: (_, record) => (
                    <div className={style.date_cell}>
                        <span className={style.time}>
                            {moment(record.occurredAt).format(TIME_FORMAT)}
                        </span>
                        <span className={style.day}>
                            {moment(record.occurredAt).format(DATE_FORMAT)}
                        </span>
                    </div>
                ),
            },
            {
                title: 'Activity',
                key: 'type',
                filters: CAMPAIGN_ACTIVITY_TYPES.map((type) => ({
                    text: TYPE_LABELS[type],
                    value: type,
                })),
                filteredValue: filters.types,
                render: (_, record) => (
                    <div className={style.activity_cell}>
                        {record.coupon && renderThumbnail(record)}
                        <div className={style.activity_text}>
                            <span className={style.activity_label}>
                                {record.coupon?.title ??
                                    TYPE_LABELS[record.type]}
                            </span>
                            <span className={style.activity_detail}>
                                {describe(record, club?.name ?? 'the campaign')}
                            </span>
                            {record.visit?.csGrant && (
                                <span className={style.audit_line}>
                                    {auditLine(record.visit.csGrant)}
                                </span>
                            )}
                        </div>
                    </div>
                ),
            },
            {
                key: 'delta',
                width: 60,
                align: 'right',
                title: () => (
                    <div className={style.add_visit_header}>
                        <Tooltip
                            title={
                                canAddVisit
                                    ? undefined
                                    : 'The client needs a confirmed phone number'
                            }
                        >
                            <span>
                                <Button
                                    variant="tertiary"
                                    disabled={!canAddVisit}
                                    onClick={() => setAddVisitOpen(true)}
                                >
                                    + Add visit
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                ),
                // Only a visit moves the counter. The joining bonus has its own
                // visit row, so tagging the Join row too would show the same +1
                // twice.
                render: (_, record) =>
                    record.coupon ? (
                        <TicketIcon />
                    ) : record.visit ? (
                        <Tag
                            variant={
                                record.visit.countedDelta === 1
                                    ? 'success'
                                    : 'grey'
                            }
                        >
                            +{record.visit.countedDelta}
                        </Tag>
                    ) : null,
            },
        ];

        const handleTableChange = (
            pagination: TablePaginationConfig,
            tableFilters: Record<string, FilterValue | null>,
            sorter:
                | SorterResult<CampaignActivityItem>
                | SorterResult<CampaignActivityItem>[]
        ) => {
            const order = Array.isArray(sorter)
                ? sorter[0]?.order
                : sorter.order;
            const types = (tableFilters.type ?? []) as CampaignActivityType[];
            const typesChanged = types.join(',') !== filters.types.join(',');

            setFilters((previous) => {
                // Two states only. AntD's cycle includes an unsorted step that
                // reports `undefined`, and mapping that to a fixed direction
                // made ascending unreachable: the click that should have left
                // descending simply re-applied it. Flipping on `undefined`
                // keeps one click = one reversal.
                const nextSort: Filters['sort'] =
                    order === 'ascend'
                        ? 'asc'
                        : order === 'descend'
                        ? 'desc'
                        : previous.sort === 'asc'
                        ? 'desc'
                        : 'asc';

                return {
                    ...previous,
                    types,
                    sort: nextSort,
                    // A changed filter or a reversed order invalidates the
                    // current page: antd keeps `pagination.current` on a sort,
                    // so someone on page 3 reversing the order got rows 21-30
                    // from the other end rather than the first ten.
                    page:
                        typesChanged || nextSort !== previous.sort
                            ? 1
                            : pagination.current ?? 1,
                    perPage: pagination.pageSize ?? previous.perPage,
                };
            });
        };

        if (isLoadingClub) {
            return (
                <div className={style.centered}>
                    <Spin />
                </div>
            );
        }

        if (!club) {
            return (
                <div className={style.activity_tab}>
                    <Alert
                        type={clubFailed ? 'error' : 'warning'}
                        showIcon
                        message={
                            clubFailed
                                ? 'Could not load the campaign. Check the connection and reopen this tab.'
                                : 'No visit-based campaign is configured in this environment.'
                        }
                    />
                </div>
            );
        }

        const handleAddVisit = async (values: {
            occurredAt: string;
            note: string | null;
            requestKey: string;
        }) => {
            if (!clientObject?.id || !clubId) {
                return;
            }
            setIsAddingVisit(true);
            const result = await clientStore.addCampaignVisit({
                clientId: clientObject.id,
                clubId,
                ...values,
            });
            setIsAddingVisit(false);

            if (!result) {
                return;
            }
            setAddVisitOpen(false);
            NotificationManager.showSuccess(
                `Visit added. This client now has ${result.countedVisits} counted visits.`
            );
            // Back to the first page: the new visit may sort anywhere, since the
            // agent chooses its date.
            // A new filters object refires the fetch effect on its own; calling
            // fetchActivity() here too fired a second, stale-closure request
            // that latestRequestId then threw away.
            setFilters((previous) => ({ ...previous, page: 1 }));
        };

        return (
            <div className={style.activity_tab}>
                <div className={style.campaign_row}>
                    <span className={style.campaign_label}>Campaign</span>
                    <Select
                        aria-label="Campaign"
                        className={style.campaign_select}
                        value={club.id}
                        options={clubs.map(({ id, name }) => ({
                            label: name,
                            value: id,
                        }))}
                        onChange={(id: number) => {
                            setSelectedClubId(id);
                            // Cleared rather than kept until the new log
                            // arrives: the rows would otherwise sit under the
                            // new campaign's name.
                            setActivity(null);
                            setActivityFailed(false);
                            setFilters((previous) => ({
                                ...previous,
                                page: 1,
                            }));
                        }}
                    />
                </div>

                {activityFailed && (
                    <Alert
                        className={style.notice}
                        type="error"
                        showIcon
                        message="Could not load the activity log. Check the connection and try again."
                    />
                )}

                {activity?.truncated && (
                    <Alert
                        className={style.notice}
                        type="warning"
                        showIcon
                        message="This client has more visits than the log can load. Only the most recent are shown."
                    />
                )}

                {!clientObject?.isPhoneNumberConfirmed && (
                    <Alert
                        className={style.notice}
                        type="info"
                        showIcon
                        message="No confirmed phone number: visits and milestones cannot be matched to this client."
                    />
                )}

                <Table<CampaignActivityItem>
                    columns={columns}
                    dataSource={activity?.results ?? []}
                    rowKey="id"
                    loading={isFetching}
                    onChange={handleTableChange}
                    pagination={{
                        current: filters.page,
                        pageSize: filters.perPage,
                        total: activity?.total ?? 0,
                        showSizeChanger: true,
                    }}
                />

                <AddVisitModal
                    open={addVisitOpen}
                    isSaving={isAddingVisit}
                    onCancel={() => setAddVisitOpen(false)}
                    onSubmit={handleAddVisit}
                />
            </div>
        );
    }
);
